// S-063-2 カレンダー取込画面（Web版ダッシュボード）
//
// Microsoft Copilot の予定要約を手動コピペ → 正規表現パース → 編集プレビュー →
// users/{uid}/calendar_events へ一時保存する。保存した予定は S-063-3 のサーバ側マッチングが
// 録音の相手先ヒントとして参照する（本画面はマッチング・Gemini 注入には関与しない）。
//
// トーン規約（C9 / DEC-009）: 件数・精度・%・confidence・「マッチ成功率」を一切表示しない。
// 「予定から相手先を補う（任意）」トーンに固定し、効果を断定する文言は出さない。
//
// plan ゲート（C12 / DEC-057 教訓）: AI 機能カテゴリのためスタンダード（¥980）限定。
// free には取込/書込 UI を無条件開放しない（UI ガード。サーバ read ゲートは S-063-3 の責務）。
//
// S-063-7 確認画面: 取込後にサーバ側マッチング（matchCalendarEventsForApproval / S-063-5）を呼び、
//   既存議事録とマッチした候補を「マッチング結果の確認」として提示する。ユーザーが承認すると
//   applyCalendarMatchApproval が meeting.title を更新し companyIds をカレンダー社名で overwrite する
//   （誤 ID 除去経路・C5/C14）。本文(minutes)・category は触らない。1タップ undo を備える（C14）。
//   C9: confidence/%/件数/「マッチ成功率」は一切出さない（提案タイトル・相手先タグの提示に留める）。
import React, { useEffect, useMemo, useState } from 'react';
import { User } from 'firebase/auth';
import { onSnapshot, orderBy, query, collection } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { PlanType, planHasAiFeatures } from '../plan';
import { Btn, Card, Icon, LockVeil } from '../components/primitives';
import { parseCopilotCalendar, ParsedEvent, ParsedCalendarResult, MAX_COPILOT_RAW_CHARS } from '../services/calendarParser';
import {
  saveEvents,
  deleteBatch,
  deleteAllEvents,
  splitReimport,
  overwriteEvents,
  EventOverwrite,
  SavedCalendarEvent,
} from '../services/calendarEventsRepo';
// S-063-15 / DEC-062: 生テキスト保管方式（RC `calendar_rawtext_enabled` true 時）。
import { splitByDate, maskPii } from '../services/calendarRawSplit';
// S-066-9 / DEC-066 真因(b): 曖昧/欠落見出しの fail-closed 隔離結果（可視化用）。
import type { AmbiguousHeader } from '../services/calendarRawSplit';
import {
  saveDay,
  saveDays,
  subscribeDays,
  deleteDay,
  deleteAllDays,
  CalendarDay,
} from '../services/calendarDaysRepo';
// S-066-8 / DEC-066 真因(a): カレンダーペースト鮮度の可視化（最終更新日・古さ注意。表示のみ）。
import { pasteFreshness, STALE_PASTE_NOTICE } from '../services/calendarFreshness';
import { useCalendarRawtextEnabled, useCalendarRetroEnabled } from '../remoteConfig';

interface Props {
  user: User;
  onBack: () => void;
  plan: PlanType;
  planLoading: boolean;
}

// プレビュー編集用の行モデル（時刻は HH:MM の編集しやすい形で保持する）。
interface EditRow {
  id: string;
  companyName: string;
  department: string;
  contactName: string;
  dateIso: string; // この行の開始日（YYYY-MM-DD）。日付見出しで解決済み。複数日一括取込で行ごとに保持。
  startHHMM: string; // '' = 未設定
  endHHMM: string; // '' = 未設定（保存時に既定会議長で補填）
  rawLine: string;
  tag: string | null;
  meetingTitle: string | null;
  recurrence: string | null;
}

// 今日（JST）の YYYY-MM-DD。
function todayJstDate(): string {
  const jst = new Date(Date.now() + 9 * 3600 * 1000);
  return `${jst.getUTCFullYear()}-${String(jst.getUTCMonth() + 1).padStart(2, '0')}-${String(
    jst.getUTCDate(),
  ).padStart(2, '0')}`;
}

// ISO（+09:00）→ 'HH:MM'。null は ''。
function isoToHHMM(iso: string | null): string {
  return iso ? iso.slice(11, 16) : '';
}

// 'HH:MM' + 対象日 → ISO（+09:00）。空なら null。
function hhmmToIso(hhmm: string, baseDate: string): string | null {
  if (!/^\d{1,2}:\d{2}$/.test(hhmm)) return null;
  const [h, m] = hhmm.split(':');
  return `${baseDate}T${h.padStart(2, '0')}:${m.padStart(2, '0')}:00+09:00`;
}

// 保存済み予定の表示用日時 'M/D HH:MM'。
function fmtSavedTime(iso: string): string {
  if (!iso || iso.length < 16) return '';
  const mo = Number(iso.slice(5, 7));
  const da = Number(iso.slice(8, 10));
  return `${mo}/${da} ${iso.slice(11, 16)}`;
}

let rowSeq = 0;
function toEditRow(e: ParsedEvent): EditRow {
  rowSeq += 1;
  return {
    id: `row-${rowSeq}`,
    companyName: e.companyName,
    department: e.department,
    contactName: e.contactName,
    dateIso: e.dateIso,
    startHHMM: isoToHHMM(e.startTime),
    endHHMM: isoToHHMM(e.endTime),
    rawLine: e.rawLine,
    tag: e.tag,
    meetingTitle: e.meetingTitle,
    recurrence: e.recurrence,
  };
}

// YYYY-MM-DD → 'M月D日（曜）'。プレビューの日付セパレータ表示用。
//   曜日は実行環境 TZ に依存しないよう UTC 基準で算出する（日付のみのため時刻ずれ無し）。
const WEEKDAY_JP = ['日', '月', '火', '水', '木', '金', '土'];
function fmtDateLabel(dateIso: string): string {
  if (!dateIso || dateIso.length < 10) return '';
  const mo = Number(dateIso.slice(5, 7));
  const da = Number(dateIso.slice(8, 10));
  const wd = WEEKDAY_JP[new Date(`${dateIso}T00:00:00Z`).getUTCDay()] ?? '';
  return `${mo}月${da}日（${wd}）`;
}

// S-063-7: matchCalendarEventsForApproval（S-063-5）が返すマッチ候補 1 件。
//   score / confidenceLabel / reason もペイロードに含まれるが、C9 により UI には一切出さない。
// S-063-19 / DEC-062: 生テキスト遡及照合（S-063-17/18）は autoApplicable / confirmReason /
//   manualEditedFields を追加で載せる（後方互換・旧構造化経路では undefined）。確認画面はこれで
//   「自動適用」「要確認」に二分し、手動編集済みの項目を保護バッジ＋上書き解除チェックで扱う。
type ConfirmReason = 'ambiguous' | 'overwrite' | 'multi_same_day';
interface MatchItem {
  projectId: string;
  meetingId: string;
  projectName: string;
  currentTitle: string;
  proposedTitle: string;
  currentCompanyIds: string[];
  recordedAt: string | null;
  matchedEvent: { eventId: string; companyName: string; department: string; contactName: string };
  autoApplicable?: boolean; // true=自動適用候補 / false=要確認隔離（AR3）/ undefined=旧構造化経路
  confirmReason?: ConfirmReason | null; // 要確認の理由（隔離理由・日本語ラベルで提示）
  // S-066-4 / DEC-065 議題2: tags（相手先）の手動編集も保護対象に含める。tags 保護は override 不可
  //   （サーバ側 applyCalendarMatchApproval が overrideProtected に関係なく append をブロックする）。
  manualEditedFields?: { title?: boolean; companyIds?: boolean; tags?: boolean } | null; // 手で直した値（保護対象）
}
interface MatchResponse {
  eligible?: boolean;
  reason?: string;
  matches?: MatchItem[];
}

// confirmReason → ユーザー向け日本語ラベル（C9: confidence/% は出さない・理由のみ）。
const CONFIRM_REASON_LABEL: Record<ConfirmReason, string> = {
  ambiguous: '候補が曖昧です',
  overwrite: '既存の値を上書きします',
  multi_same_day: '同日に複数の会議があります',
};

// 手動編集フラグが 1 つでも立っているか（保護バッジ表示・既定スキップの判定）。
// S-066-4: tags（相手先）の手動編集も含める＝手で直した相手先を予定表で勝手に変えない。
function hasManualEdit(m: MatchItem): boolean {
  const mef = m.manualEditedFields;
  return !!mef && (mef.title === true || mef.companyIds === true || mef.tags === true);
}

// S-066-4: 「保護を解除して上書き」で上書きできるフィールド（title / companyIds）が編集済みか。
//   tags の手動編集は override 不可なので、tags だけが編集済みのときは上書きチェックを出さない。
function hasOverridableEdit(m: MatchItem): boolean {
  const mef = m.manualEditedFields;
  return !!mef && (mef.title === true || mef.companyIds === true);
}

// S-066-4: 相手先（tags）が手動編集済みか＝予定表からの相手先付与をスキップする（恒久保護）。
function hasManualTagEdit(m: MatchItem): boolean {
  return m.manualEditedFields?.tags === true;
}

// マッチ候補の一意キー（projectId/meetingId）。選択状態・承認送信のキーに使う。
function matchKey(m: { projectId: string; meetingId: string }): string {
  return `${m.projectId}/${m.meetingId}`;
}

// S-063-18: 1 取込分の遡及更新をまとめて undo するためのバッチ ID。crypto.randomUUID が無い環境（古い
//   ブラウザ・jsdom）でも壊れないようフォールバックを持つ。サーバ側で 64 字に切られる前提。
function newRetroBatchId(): string {
  try {
    const c: any = (typeof crypto !== 'undefined') ? crypto : null;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  } catch (_) { /* noop */ }
  return `b-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// 録音日時 ISO（+09:00 or UTC Z）→ JST 'M月D日（曜）'。実行環境 TZ 非依存（epoch + 9h を UTC で読む）。
function fmtMeetingDate(iso: string | null): string {
  if (!iso) return '';
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '';
  const jst = new Date(ms + 9 * 3600 * 1000);
  const wd = WEEKDAY_JP[jst.getUTCDay()] ?? '';
  return `${jst.getUTCMonth() + 1}月${jst.getUTCDate()}日（${wd}）`;
}

// 行が要確認か（会社名空 or 開始時刻空）。
function rowNeedsReview(r: EditRow): boolean {
  return !r.companyName.trim() || !r.startHHMM.trim();
}
// 行が保存対象か（会社名・開始時刻が揃っている）。
function rowWritable(r: EditRow): boolean {
  return !!r.companyName.trim() && !!r.startHHMM.trim();
}

const fieldStyle: React.CSSProperties = {
  padding: '6px 8px',
  borderRadius: 'var(--r-2)',
  background: 'var(--bg-hover)',
  border: '1px solid var(--border-strong)',
  color: 'var(--fg)',
  font: 'var(--type-caption)',
  outline: 'none',
  minWidth: 0,
};

const CalendarImportPage: React.FC<Props> = ({ user, onBack, plan, planLoading }) => {
  const canUse = !planLoading && planHasAiFeatures(plan);

  const [raw, setRaw] = useState('');
  const [rows, setRows] = useState<EditRow[] | null>(null); // null = 未パース
  const [parseErrors, setParseErrors] = useState<{ line: string; reason: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [reading, setReading] = useState(false); // S-063-12: Gemini 読み取り中ローディング
  const [toast, setToast] = useState<string | null>(null);
  const [saved, setSaved] = useState<SavedCalendarEvent[]>([]);

  // S-063-7 確認画面の状態。view='confirm' のときマッチ一覧を出す。
  // S-063-9: view='overwrite' のとき再取込の同時間帯重複の上書き確認を出す。
  const [view, setView] = useState<'import' | 'confirm' | 'overwrite'>('import');
  const [matches, setMatches] = useState<MatchItem[]>([]);
  const [selected, setSelected] = useState<Record<string, boolean>>({}); // matchKey → 承認するか
  // S-063-19: 手動編集済み（保護）項目を「保護を解除して上書き」したか（matchKey → overrideProtected）。
  const [overrideSel, setOverrideSel] = useState<Record<string, boolean>>({});
  // 直近に反映したマッチ（undo 対象）。反映後に「元に戻す」導線を出す（C14）。
  const [undoTargets, setUndoTargets] = useState<{ projectId: string; meetingId: string }[]>([]);
  const [undoBatchId, setUndoBatchId] = useState<string>(''); // S-063-18: バッチ undo の retroBatchId

  // S-063-9: 再取込で検出した同時間帯の上書き候補と、その選択状態（既定は全て上書き）。
  const [overwrites, setOverwrites] = useState<EventOverwrite[]>([]);
  const [overwriteSel, setOverwriteSel] = useState<Record<string, boolean>>({}); // existingId → 上書きするか
  // 上書き確認と同時に保存済みの fresh バッチ。上書き確定後のマッチングで使う。
  const [pendingBatchId, setPendingBatchId] = useState<string>('');

  // ───────────── S-063-15 生テキスト保管方式の状態（RC `calendar_rawtext_enabled` true 時のみ使用） ─────────────
  const rawMode = useCalendarRawtextEnabled();
  // 生テキスト保存後に遡及照合（要件 B）を起動してよいか（既定 false=保存のみ・撤退弁）。
  const retroEnabled = useCalendarRetroEnabled();
  // 日付分割プレビュー（編集可）。null = 未分割。各日 text は PII マスク後。
  const [dayDrafts, setDayDrafts] = useState<{ dayId: string; text: string }[] | null>(null);
  // 日付ヘッダより前の「日付なし」行。日付を割り当てるか破棄する。
  const [undatedDraft, setUndatedDraft] = useState('');
  const [undatedAssignDate, setUndatedAssignDate] = useState('');
  // S-066-9: 検出した曖昧/欠落見出し（fail-closed で「日付なし」に隔離した分の警告表示用）。
  const [ambiguousHeaders, setAmbiguousHeaders] = useState<AmbiguousHeader[]>([]);
  // 保存済みの日付別生テキスト（閲覧・編集・削除）。
  const [savedDays, setSavedDays] = useState<CalendarDay[]>([]);
  // 保存済み 1 日分のインライン編集状態。
  const [editDayId, setEditDayId] = useState<string | null>(null);
  const [editDayText, setEditDayText] = useState('');

  // 保存済み予定の購読（standard かつ旧構造化経路のときだけ read を発火させる / C12）。
  //   rawMode（生テキスト経路）では calendar_events を読まない（無駄な read 回避・並存）。
  useEffect(() => {
    if (!canUse || rawMode) {
      setSaved([]);
      return;
    }
    const col = collection(db, 'users', user.uid, 'calendar_events');
    const unsub = onSnapshot(
      query(col, orderBy('seq', 'desc')),
      (snap) => {
        setSaved(
          snap.docs.map((d) => {
            const data = d.data() as any;
            return {
              id: d.id,
              companyName: data.companyName || '',
              department: data.department || '',
              contactName: data.contactName || '',
              startTime: data.startTime || '',
              endTime: data.endTime || '',
              batchId: data.batchId || '',
              seq: typeof data.seq === 'number' ? data.seq : 0,
            };
          }),
        );
      },
      (e) => {
        console.error('[CalendarImport] failed to read calendar_events:', e);
      },
    );
    return unsub;
  }, [canUse, rawMode, user.uid]);

  // S-063-15: 保存済み日付別生テキストの購読（rawMode かつ standard のときだけ read を発火 / C12）。
  useEffect(() => {
    if (!rawMode || !canUse) {
      setSavedDays([]);
      return;
    }
    return subscribeDays(user.uid, setSavedDays);
  }, [rawMode, canUse, user.uid]);

  // トーストは数秒で自動的に消す。
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);

  const trimmedRaw = raw.trim();
  // 入力上限超過の事前検知。サーバ/フォールバック双方が MAX_COPILOT_RAW_CHARS で切り捨てるため、
  // 超過時は読み取り前にユーザーへ警告する（サイレント切り捨て防止。1ヶ月分一括ペースト想定）。
  const overLimit = raw.length > MAX_COPILOT_RAW_CHARS;
  const canParse = canUse && trimmedRaw.length > 0;

  const writableCount = useMemo(
    () => (rows ? rows.filter(rowWritable).length : 0),
    [rows],
  );

  // S-063-12 / DEC-061: 読み取りの主経路を Gemini callable（parseCalendarText）に切替。
  //   自然文 Copilot 出力でも会社名/部署/担当/日時を構造化抽出する。callable 失敗時は
  //   既存の正規表現パーサー（parseCopilotCalendar）にフォールバックして機能を途切れさせない（CP3）。
  const handleParse = async () => {
    if (!canParse || reading) return;
    const baseDate = todayJstDate();
    setReading(true);
    let result: ParsedCalendarResult;
    let usedFallback = false;
    try {
      const fn = httpsCallable<{ raw: string; baseDateIso: string }, ParsedCalendarResult>(
        functions, 'parseCalendarText',
      );
      result = (await fn({ raw, baseDateIso: baseDate })).data;
    } catch (e) {
      // CP3: Gemini callable 不達/quota/壊れ応答 → ローカル正規表現でフォールバック。
      console.warn('[CalendarImport] parseCalendarText failed, using regex fallback:', e);
      result = parseCopilotCalendar(raw, baseDate);
      usedFallback = true;
    } finally {
      setReading(false);
    }
    setRows(result.events.map(toEditRow));
    setParseErrors(result.parseErrors);
    if (usedFallback) setToast('かんたんモードで読み取りました');
  };

  const updateRow = (id: string, patch: Partial<EditRow>) => {
    setRows((prev) => (prev ? prev.map((r) => (r.id === id ? { ...r, ...patch } : r)) : prev));
  };
  const removeRow = (id: string) => {
    setRows((prev) => (prev ? prev.filter((r) => r.id !== id) : prev));
  };

  const handleSave = async () => {
    if (!canUse || !rows) return;
    // EditRow → ParsedEvent（保存対象は repo 側 filter で会社名・時刻揃いのみ）。
    const events: ParsedEvent[] = rows.map((r) => {
      // 行ごとの日付（日付見出しで解決済み）を使う。未解決なら今日（JST）にフォールバック。
      const rowDate = r.dateIso || todayJstDate();
      return {
        companyName: r.companyName.trim(),
        department: r.department.trim(),
        contactName: r.contactName.trim(),
        dateIso: rowDate,
        startTime: hhmmToIso(r.startHHMM.trim(), rowDate),
        endTime: hhmmToIso(r.endHHMM.trim(), rowDate),
        tag: r.tag,
        meetingTitle: r.meetingTitle,
        recurrence: r.recurrence,
        rawLine: r.rawLine,
        needsReview: false,
      };
    });
    setBusy(true);
    try {
      // S-063-9: 既存予定（永続保存・saved）と突き合わせ、同時間帯の重複を検出する。
      //   fresh（重複なし）は通常保存、conflicts（同時間帯×内容相違）は上書き確認 UI へ、
      //   identical（同時間帯×内容一致）は重複作成も上書きもしない（鮮度はそのまま）。
      const split = splitReimport(events, saved);
      let batchId = '';
      if (split.fresh.length > 0) {
        const r = await saveEvents(user.uid, split.fresh);
        batchId = r.batchId;
      }

      // 取込フォームはここでクリア（fresh 保存済み・conflicts は別 UI で扱う）。
      setRows(null);
      setRaw('');
      setParseErrors([]);

      if (split.conflicts.length > 0) {
        // 上書き確認へ。既定は全件上書き（ユーザーが外す）。fresh の batchId は確定後のマッチングに使う。
        setOverwrites(split.conflicts);
        const sel: Record<string, boolean> = {};
        split.conflicts.forEach((o) => { sel[o.existingId] = true; });
        setOverwriteSel(sel);
        setPendingBatchId(batchId);
        setView('overwrite');
        return;
      }

      // 重複なし。fresh があればマッチング、無ければ（全 identical 等）取込完了トーストのみ。
      if (batchId) {
        await runMatching({ batchId });
      } else {
        setToast('予定はすでに最新です');
      }
    } catch (e: any) {
      setToast('取り込みできませんでした。もう一度お試しください。');
      console.error('[CalendarImport] save failed:', e);
    } finally {
      setBusy(false);
    }
  };

  // S-063-9: 上書き確認の確定。承認された既存 doc を新しい内容で上書きし、その後マッチングへ。
  const handleConfirmOverwrite = async () => {
    const approved = overwrites.filter((o) => overwriteSel[o.existingId]);
    const batchId = pendingBatchId;
    setBusy(true);
    try {
      if (approved.length > 0) {
        await overwriteEvents(user.uid, approved);
      }
    } catch (e: any) {
      console.error('[CalendarImport] overwrite failed:', e);
      setToast('上書きできませんでした。もう一度お試しください。');
    } finally {
      setBusy(false);
    }
    // 状態を片付けて取込画面へ戻す。
    setOverwrites([]);
    setOverwriteSel({});
    setPendingBatchId('');
    setView('import');
    // 上書き or fresh があればマッチング（上書き分は createdAt 順で拾えるよう batchId 指定なしの直近対象で照合）。
    if (approved.length > 0) {
      await runMatching({});
    } else if (batchId) {
      await runMatching({ batchId });
    } else {
      setToast('予定はそのままにしました');
    }
  };

  // S-063-7 / S-063-19: サーバ側マッチング（構造化 batchId 経路 or 生テキスト遡及 dayIds 経路）を呼び、
  //   マッチがあれば確認画面に遷移。無ければトーストのみ。任意の補助機能なので失敗は握りつぶす（C9）。
  //   既定選択は autoApplicable で分岐: 自動適用可=承認ON / 要確認=承認OFF（誤マッチ防止・AR3）。
  //   旧構造化経路（autoApplicable 未設定）は従来どおり全件 ON。
  const runMatching = async (
    params: { batchId?: string; dayIds?: string[] },
    noMatchToast = '予定を取り込みました',
  ) => {
    try {
      const fn = httpsCallable<{ batchId?: string; dayIds?: string[] }, MatchResponse>(
        functions, 'matchCalendarEventsForApproval',
      );
      const payload: { batchId?: string; dayIds?: string[] } = {};
      if (params.batchId) payload.batchId = params.batchId;
      if (params.dayIds && params.dayIds.length > 0) payload.dayIds = params.dayIds;
      const res = await fn(payload);
      // DEC-066 follow-up: サーバ側の遡及照合が休眠中（retro_disabled）は無音の空振りにせず明示する。
      //   保存自体は成功しているので保存文言を維持しつつ、過去分への反映が走らないことを伝える。
      if (res.data && res.data.eligible === false && res.data.reason === 'retro_disabled') {
        setToast('予定を保存しました（過去の議事録への反映は現在オフになっています）');
        return;
      }
      const ms = Array.isArray(res.data && res.data.matches) ? (res.data.matches as MatchItem[]) : [];
      if (ms.length > 0) {
        setMatches(ms);
        const sel: Record<string, boolean> = {};
        // 要確認は既定OFF。S-066-4: 手動編集済み（title/companyIds/tags のいずれか）も既定OFF＝
        //   手で直した値を予定表で勝手に上書きしない（DEC-064 議題1条件②の manualEditedFields 既定スキップを UI に反映）。
        ms.forEach((m) => { sel[matchKey(m)] = m.autoApplicable !== false && !hasManualEdit(m); });
        setSelected(sel);
        setOverrideSel({}); // 保護解除は既定なし（ユーザーが明示）。
        setView('confirm');
      } else {
        setToast(noMatchToast);
      }
    } catch (e) {
      console.error('[CalendarImport] matching failed:', e);
      setToast(noMatchToast);
    }
  };

  const toggleMatch = (key: string) => {
    setSelected((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const approveAll = () => {
    const sel: Record<string, boolean> = {};
    matches.forEach((m) => { sel[matchKey(m)] = true; });
    setSelected(sel);
  };
  const skipAll = () => {
    const sel: Record<string, boolean> = {};
    matches.forEach((m) => { sel[matchKey(m)] = false; });
    setSelected(sel);
  };

  // S-063-9: 上書き候補の選択トグル/一括操作。
  const toggleOverwrite = (id: string) => {
    setOverwriteSel((prev) => ({ ...prev, [id]: !prev[id] }));
  };
  const overwriteAll = () => {
    const sel: Record<string, boolean> = {};
    overwrites.forEach((o) => { sel[o.existingId] = true; });
    setOverwriteSel(sel);
  };
  const keepAll = () => {
    const sel: Record<string, boolean> = {};
    overwrites.forEach((o) => { sel[o.existingId] = false; });
    setOverwriteSel(sel);
  };

  // 承認されたマッチをサーバへ反映（title 更新 + companyIds overwrite）。完了後 undo 導線を出す。
  const handleConfirm = async () => {
    const approvals = matches
      .filter((m) => selected[matchKey(m)])
      .map((m) => {
        const base = {
          projectId: m.projectId,
          meetingId: m.meetingId,
          companyName: m.matchedEvent.companyName,
          proposedTitle: m.proposedTitle,
          eventId: m.matchedEvent.eventId,
        };
        // S-063-19: 手動編集済みを「保護を解除して上書き」したときだけ overrideProtected を載せる
        //   （未指定なら apply 側が手動値を保護する・AR4/AR5）。
        return overrideSel[matchKey(m)] ? { ...base, overrideProtected: true } : base;
      });
    if (approvals.length === 0) {
      // 全スキップ＝何もせず取込画面へ戻す。
      setView('import');
      setMatches([]);
      return;
    }
    setBusy(true);
    // S-063-18: この取込分をまとめて undo できるよう retroBatchId を発行し apply に渡す。
    const retroBatchId = newRetroBatchId();
    try {
      const fn = httpsCallable<{ approvals: typeof approvals; retroBatchId: string }, { applied: number; results: Array<{ projectId: string; meetingId: string; ok: boolean }> }>(
        functions, 'applyCalendarMatchApproval',
      );
      const res = await fn({ approvals, retroBatchId });
      const okTargets = (res.data && Array.isArray(res.data.results) ? res.data.results : [])
        .filter((r) => r.ok && r.projectId && r.meetingId)
        .map((r) => ({ projectId: r.projectId, meetingId: r.meetingId }));
      setUndoTargets(okTargets);
      setUndoBatchId(okTargets.length > 0 ? retroBatchId : '');
      setView('import');
      setMatches([]);
      setToast(okTargets.length > 0 ? 'タイトルと相手先を更新しました' : '反映できる項目がありませんでした');
    } catch (e: any) {
      console.error('[CalendarImport] apply failed:', e);
      const msg: string = (e && e.message) || '';
      setToast(msg.includes('PLAN_REQUIRED_STANDARD')
        ? 'スタンダードプランでご利用いただけます'
        : '反映できませんでした。もう一度お試しください。');
    } finally {
      setBusy(false);
    }
  };

  // C14 / S-063-18: 直近の反映を1タップで元に戻す（更新前の title/companyIds を復元）。
  //   retroBatchId があれば undoCalendarRetroBatch でこの取込分だけをまとめて戻す（別取込の meeting を
  //   取り違えない）。retroBatchId が無い旧経路は従来の単件リスト undo にフォールバック。
  const handleUndo = async () => {
    if (undoTargets.length === 0) return;
    setBusy(true);
    try {
      if (undoBatchId) {
        const fn = httpsCallable<{ retroBatchId: string; meetings: typeof undoTargets }, { reverted: number }>(
          functions, 'undoCalendarRetroBatch',
        );
        await fn({ retroBatchId: undoBatchId, meetings: undoTargets });
      } else {
        const fn = httpsCallable<{ meetings: typeof undoTargets }, { reverted: number }>(
          functions, 'undoCalendarMatchApproval',
        );
        await fn({ meetings: undoTargets });
      }
      setUndoTargets([]);
      setUndoBatchId('');
      setToast('元に戻しました');
    } catch (e) {
      console.error('[CalendarImport] undo failed:', e);
      setToast('元に戻せませんでした。もう一度お試しください。');
    } finally {
      setBusy(false);
    }
  };

  // ───────────── S-063-15 生テキスト保管方式のハンドラ ─────────────

  // 貼付テキストを日付ヘッダで分割し、PII マスク済みプレビューを作る（Gemini 不使用・純関数）。
  const handleSplit = () => {
    if (!canUse) return;
    const base = todayJstDate();
    const { days, undated, ambiguous } = splitByDate(raw, base);
    setDayDrafts(days.map((d) => ({ dayId: d.dayId, text: maskPii(d.rawText) })));
    setUndatedDraft(maskPii(undated));
    setUndatedAssignDate('');
    setAmbiguousHeaders(ambiguous);
  };

  // プレビューの 1 日分テキストを編集する。
  const updateDayDraft = (dayId: string, text: string) => {
    setDayDrafts((prev) => (prev ? prev.map((d) => (d.dayId === dayId ? { ...d, text } : d)) : prev));
  };
  const removeDayDraft = (dayId: string) => {
    setDayDrafts((prev) => (prev ? prev.filter((d) => d.dayId !== dayId) : prev));
  };

  // 分割プレビューを calendar_days に保存（docID=日付・同日上書き）。
  const handleSaveDays = async () => {
    if (!canUse || !dayDrafts) return;
    const toSave = dayDrafts
      .filter((d) => d.text.trim())
      .map((d) => ({ dayId: d.dayId, rawText: d.text }));
    // 「日付なし」に日付を割り当てた分を加える（同日が既にあればマージ・無ければ新規）。
    if (undatedDraft.trim() && /^\d{4}-\d{2}-\d{2}$/.test(undatedAssignDate)) {
      const existing = toSave.find((d) => d.dayId === undatedAssignDate);
      if (existing) existing.rawText = `${existing.rawText}\n${undatedDraft}`.trim();
      else toSave.push({ dayId: undatedAssignDate, rawText: undatedDraft });
    }
    if (toSave.length === 0) {
      setToast('保存できる予定がありませんでした');
      return;
    }
    setBusy(true);
    try {
      const savedIds = await saveDays(user.uid, toSave);
      setDayDrafts(null);
      setUndatedDraft('');
      setUndatedAssignDate('');
      setAmbiguousHeaders([]);
      setRaw('');
      // S-063-17/19: RC `calendar_retro_enabled` が true なら、保存した日付分の過去議事録を遡及照合し、
      //   自動適用/要確認の二分プレビューへ遷移する。off（既定）や保存ゼロ件なら保存トーストのみ（撤退弁）。
      if (retroEnabled && savedIds.length > 0) {
        await runMatching({ dayIds: savedIds }, '予定を保存しました');
      } else {
        setToast(savedIds.length > 0 ? '予定を保存しました' : '保存できる予定がありませんでした');
      }
    } catch (e) {
      console.error('[CalendarImport] saveDays failed:', e);
      setToast('保存できませんでした。もう一度お試しください。');
    } finally {
      setBusy(false);
    }
  };

  // 保存済み 1 日分の編集開始/取消/保存。
  const startEditDay = (d: CalendarDay) => {
    setEditDayId(d.dayId);
    setEditDayText(d.rawText);
  };
  const cancelEditDay = () => {
    setEditDayId(null);
    setEditDayText('');
  };
  const handleSaveEditDay = async () => {
    if (!editDayId) return;
    setBusy(true);
    try {
      const ok = await saveDay(user.uid, editDayId, editDayText);
      setToast(ok ? '更新しました' : '内容が空です。削除する場合は削除ボタンをお使いください。');
      if (ok) {
        setEditDayId(null);
        setEditDayText('');
      }
    } catch (e) {
      console.error('[CalendarImport] saveDay (edit) failed:', e);
      setToast('更新できませんでした。もう一度お試しください。');
    } finally {
      setBusy(false);
    }
  };
  const handleDeleteDay = async (dayId: string) => {
    setBusy(true);
    try {
      await deleteDay(user.uid, dayId);
      if (editDayId === dayId) cancelEditDay();
    } catch (e) {
      console.error('[CalendarImport] deleteDay failed:', e);
      setToast('削除できませんでした。もう一度お試しください。');
    } finally {
      setBusy(false);
    }
  };
  const handleDeleteAllDays = async () => {
    if (!window.confirm('保存済みの予定をすべて削除しますか？')) return;
    setBusy(true);
    try {
      await deleteAllDays(user.uid);
    } catch (e) {
      console.error('[CalendarImport] deleteAllDays failed:', e);
      setToast('削除できませんでした。もう一度お試しください。');
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteBatch = async (batchId: string) => {
    setBusy(true);
    try {
      await deleteBatch(user.uid, batchId);
    } catch (e) {
      console.error('[CalendarImport] deleteBatch failed:', e);
      setToast('削除できませんでした。もう一度お試しください。');
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm('保存済みの予定をすべて削除しますか？')) return;
    setBusy(true);
    try {
      await deleteAllEvents(user.uid);
    } catch (e) {
      console.error('[CalendarImport] deleteAll failed:', e);
      setToast('削除できませんでした。もう一度お試しください。');
    } finally {
      setBusy(false);
    }
  };

  // 相手先タグ（会社/部署/担当）チップ。C9: confidence/% は出さない。
  const tagChip = (text: string, kind: 'company' | 'dept' | 'person') => {
    const palette = {
      company: { bg: 'var(--accent-fill)', fg: 'var(--teal-300)' },
      dept: { bg: 'var(--overlay-soft)', fg: 'var(--fg-secondary)' },
      person: { bg: 'var(--cta-fill)', fg: 'var(--amber-400)' },
    }[kind];
    return (
      <span key={kind + text} style={{
        font: 'var(--type-caption)', padding: '2px 8px', borderRadius: 'var(--r-1)',
        background: palette.bg, color: palette.fg, fontWeight: 500, whiteSpace: 'nowrap',
      }}>{text}</span>
    );
  };

  // C14 / S-063-18: 直近の反映を1タップで元に戻す導線（反映後に表示・旧/生テキスト両経路で共用）。
  const renderUndoBanner = () =>
    undoTargets.length > 0 ? (
      <Card pad={12} style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, background: 'var(--accent-fill-faint)', border: '1px solid var(--accent-line)' }}>
        <Icon name="check" size={16} style={{ color: 'var(--accent)' }} />
        <span style={{ flex: 1, font: 'var(--type-ui)', color: 'var(--fg)' }}>
          議事録のタイトルと相手先を更新しました
        </span>
        <Btn kind="ghost" size="sm" icon="rotate-ccw" disabled={busy} onClick={handleUndo}>元に戻す</Btn>
        <button onClick={() => setUndoTargets([])} aria-label="閉じる"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--fg-tertiary)', display: 'inline-flex', padding: 4 }}>
          <Icon name="x" size={14} />
        </button>
      </Card>
    ) : null;

  // S-063-19: マッチ候補 1 件のカード（自動適用分・要確認分で共通）。
  //   推測バッジ（RT3）・要確認理由（confirmReason の日本語ラベル）・手動編集保護（保護解除チェック）を表示する。
  const renderMatchCard = (m: MatchItem) => {
    const key = matchKey(m);
    const isApproved = !!selected[key];
    const isOverride = Array.isArray(m.currentCompanyIds) && m.currentCompanyIds.length > 0;
    const titleChanges = m.proposedTitle && m.proposedTitle !== m.currentTitle;
    const ev = m.matchedEvent;
    const protectedEdit = hasManualEdit(m); // 手で直した値あり → 既定で保護
    const overridableEdit = hasOverridableEdit(m); // title/companyIds が編集済み → 上書き解除チェックを出す
    const manualTagEdit = hasManualTagEdit(m); // 相手先(tags)が編集済み → 付与しない（恒久保護・override 不可）
    const needsConfirm = m.autoApplicable === false; // 要確認カード → 確認トーンを添える（DEC-065 議題2条件④）
    const overriding = !!overrideSel[key];
    const reasonLabel = m.confirmReason ? CONFIRM_REASON_LABEL[m.confirmReason] : '';
    return (
      <Card
        key={key}
        pad={0}
        style={{
          overflow: 'hidden',
          border: isApproved ? '1px solid var(--accent)' : '1px solid var(--border)',
          background: isApproved ? 'var(--accent-fill-faint)' : undefined,
          opacity: isApproved ? 1 : 0.66,
        }}
      >
        {/* 上書き警告（既存の相手先を上書きするとき）。S-066-4: 「タグ」語を出さず「相手先」表記（DEC-009）。 */}
        {isOverride && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px',
            background: 'var(--cta-fill)', borderBottom: '1px solid var(--cta-line)',
          }}>
            <Icon name="alert-circle" size={14} style={{ color: 'var(--amber-400)' }} />
            <span style={{ font: 'var(--type-caption)', color: 'var(--amber-400)' }}>
              既存の相手先を上書きします
            </span>
          </div>
        )}

        {/* ヘッダ行: チェックボックス + 日付 + 議事録名 + 推測バッジ */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px' }}>
          <button
            onClick={() => toggleMatch(key)}
            aria-label={isApproved ? 'このマッチの承認を外す' : 'このマッチを承認する'}
            aria-pressed={isApproved}
            style={{
              width: 20, height: 20, flexShrink: 0, borderRadius: 'var(--r-1)', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              background: isApproved ? 'var(--accent)' : 'transparent',
              border: '2px solid ' + (isApproved ? 'var(--accent)' : 'var(--border-strong)'),
              color: 'var(--on-cta)', padding: 0,
            }}
          >
            {isApproved && <Icon name="check" size={12} />}
          </button>
          <span style={{
            font: 'var(--type-caption)', fontWeight: 600, padding: '4px 10px', borderRadius: 'var(--r-1)',
            background: 'var(--accent-fill)', color: 'var(--teal-300)', whiteSpace: 'nowrap',
          }}>{fmtMeetingDate(m.recordedAt) || '日付不明'}</span>
          <span style={{
            font: 'var(--type-caption)', color: 'var(--fg-secondary)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
          }}>{m.currentTitle || '（無題の議事録）'}{m.projectName ? ` ・ ${m.projectName}` : ''}</span>
          {/* RT3: 推測であることを明示するバッジ（AI 一発確定にしない）。 */}
          <span style={{
            font: 'var(--type-caption)', padding: '2px 8px', borderRadius: 'var(--r-1)',
            background: 'var(--overlay-soft)', color: 'var(--fg-tertiary)', whiteSpace: 'nowrap', flexShrink: 0,
          }}>予定表から自動推測</span>
        </div>

        {/* 要確認理由 + 手動編集保護バッジ（C9: confidence/% は出さない・理由のみ） */}
        {(reasonLabel || protectedEdit) && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '0 16px 8px 48px' }}>
            {reasonLabel && (
              <span style={{
                font: 'var(--type-caption)', padding: '2px 8px', borderRadius: 'var(--r-1)',
                background: 'var(--cta-fill)', color: 'var(--amber-400)', fontWeight: 500,
              }}>確認: {reasonLabel}</span>
            )}
            {protectedEdit && (
              <span style={{
                font: 'var(--type-caption)', padding: '2px 8px', borderRadius: 'var(--r-1)',
                background: 'var(--overlay-soft)', color: 'var(--fg-secondary)', fontWeight: 500,
                display: 'inline-flex', alignItems: 'center', gap: 4,
              }}>
                <Icon name="lock" size={11} />手動編集済み（保護）
              </span>
            )}
          </div>
        )}

        {/* 本体: 相手先 + タイトル変更プレビュー。S-066-4: 「タグ」語を出さず「相手先」表記（DEC-009）。 */}
        <div style={{ padding: '0 16px 12px 48px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {/* S-066-4 / DEC-065 議題2条件④: 要確認カードは確認トーンを添える（DEC-050 必達7・断定しない）。 */}
          {needsConfirm && !manualTagEdit && (
            <div style={{ font: 'var(--type-ui)', color: 'var(--fg)', fontWeight: 600 }}>
              この相手先でよろしいですか
            </div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {ev.companyName && tagChip(ev.companyName, 'company')}
            {ev.department && tagChip(ev.department, 'dept')}
            {ev.contactName && tagChip(ev.contactName, 'person')}
          </div>
          {/* S-066-4: 相手先(tags)を手で直した議事録は予定表で変えない（恒久保護・override 不可）。 */}
          {manualTagEdit && (
            <div style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)' }}>
              編集済みの相手先はそのまま保持します
            </div>
          )}
          {titleChanges ? (
            <div>
              <div style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)' }}>タイトル変更</div>
              <div style={{ font: 'var(--type-ui)', color: 'var(--fg)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <Icon name="arrow-right" size={14} style={{ color: 'var(--accent)' }} />
                {m.proposedTitle}
              </div>
            </div>
          ) : (
            <div style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)' }}>
              タイトルは変わりません（相手先のみ更新）
            </div>
          )}
        </div>

        {/* 手動編集保護の上書き解除（明示操作のみ・既定は保護）。
            S-066-4: title/companyIds が編集済みのときだけ表示＝tags のみ編集は override 不可なので出さない。 */}
        {overridableEdit && (
          <label style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '0 16px 12px 48px',
            font: 'var(--type-caption)', color: 'var(--fg-secondary)', cursor: 'pointer',
          }}>
            <input
              type="checkbox"
              checked={overriding}
              disabled={busy}
              onChange={(e) => setOverrideSel((p) => ({ ...p, [key]: e.target.checked }))}
              aria-label="保護を解除して上書き"
            />
            保護を解除して上書き
          </label>
        )}

        {/* 個別 承認 / スキップ */}
        <div style={{ display: 'flex', gap: 8, padding: '0 16px 12px 48px' }}>
          <Btn kind={isApproved ? 'cta' : 'solid'} size="sm" disabled={busy}
            onClick={() => setSelected((p) => ({ ...p, [key]: true }))}>
            {isOverride ? '承認（上書き）' : '承認'}
          </Btn>
          <Btn kind="ghost" size="sm" disabled={busy}
            onClick={() => setSelected((p) => ({ ...p, [key]: false }))}>
            {isOverride ? '現在を維持' : 'スキップ'}
          </Btn>
        </div>
      </Card>
    );
  };

  // セクション見出し（二分表示の各グループ）。
  const sectionHeader = (title: string, sub: string) => (
    <div style={{ marginTop: 4 }}>
      <h3 style={{ font: 'var(--type-h3)', color: 'var(--fg)', fontWeight: 700, marginBottom: 2 }}>{title}</h3>
      <p style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)' }}>{sub}</p>
    </div>
  );

  // S-063-7 / S-063-19 確認画面。autoApplicable で「自動適用」「要確認」に二分表示する。
  //   旧構造化経路（autoApplicable 未設定）は要確認が 0 件のため従来どおり単一リストになる。
  const renderConfirm = () => {
    const autoMatches = matches.filter((m) => m.autoApplicable !== false);
    const confirmMatches = matches.filter((m) => m.autoApplicable === false);
    const grouped = confirmMatches.length > 0; // 要確認が 1 件以上 → 二分表示
    return (
      <>
        <button
          onClick={() => { setView('import'); setMatches([]); }}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none',
            color: 'var(--fg-tertiary)', font: 'var(--type-ui)', cursor: 'pointer', marginBottom: 16, padding: 0,
          }}
        >
          <Icon name="arrow-left" size={16} />カレンダー取り込みに戻る
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <Icon name="git-compare" size={20} style={{ color: 'var(--accent)' }} />
          <h1 style={{ font: 'var(--type-h2)', color: 'var(--fg)' }}>マッチング結果の確認</h1>
        </div>
        <p style={{ font: 'var(--type-body)', color: 'var(--fg-secondary)', marginBottom: 16 }}>
          取り込んだ予定と既存の議事録を照合しました。承認すると、議事録のタイトルと相手先が更新されます。本文は変わりません。
        </p>

        {/* 一括操作 */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
          <Btn kind="accent" size="sm" icon="check" disabled={busy} onClick={approveAll}>すべて承認</Btn>
          <Btn kind="ghost" size="sm" disabled={busy} onClick={skipAll}>すべてスキップ</Btn>
        </div>

        {/* マッチ一覧（二分表示 or 単一リスト） */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingBottom: 84 }}>
          {grouped ? (
            <>
              {autoMatches.length > 0 && sectionHeader('そのまま反映できる予定', '予定表から相手先がはっきり分かった議事録です。既定で反映対象になっています。')}
              {autoMatches.map(renderMatchCard)}
              {sectionHeader('確認してから反映する予定', '相手先が複数候補だったり、既存の値を上書きする予定です。承認したものだけ反映します。')}
              {confirmMatches.map(renderMatchCard)}
            </>
          ) : (
            autoMatches.map(renderMatchCard)
          )}
        </div>

        {/* フッタ: 確定 */}
        <div style={{
          position: 'sticky', bottom: 0, marginTop: 4, padding: '12px 0',
          background: 'linear-gradient(180deg, transparent, var(--bg) 30%)',
          display: 'flex', justifyContent: 'flex-end',
        }}>
          <Btn kind="cta" icon="check" disabled={busy} onClick={handleConfirm}>確定して反映</Btn>
        </div>
      </>
    );
  };

  // S-063-9 上書き確認画面（再取込で同時間帯の既存予定が見つかったとき）。
  const renderOverwrite = () => {
    const approvedCount = overwrites.filter((o) => overwriteSel[o.existingId]).length;
    // 取込予定（incoming）1 件の表示行（会社・部署・担当）。
    const partyLine = (companyName: string, department: string, contactName: string) => (
      <span style={{ font: 'var(--type-ui)', color: 'var(--fg)' }}>
        {companyName || '（会社名なし）'}
        {department && <span style={{ color: 'var(--fg-tertiary)' }}> ・ {department}</span>}
        {contactName && <span style={{ color: 'var(--fg-tertiary)' }}> ・ {contactName}</span>}
      </span>
    );
    return (
      <>
        <button
          onClick={() => { setView('import'); setOverwrites([]); setOverwriteSel({}); setPendingBatchId(''); }}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none',
            color: 'var(--fg-tertiary)', font: 'var(--type-ui)', cursor: 'pointer', marginBottom: 16, padding: 0,
          }}
        >
          <Icon name="arrow-left" size={16} />カレンダー取り込みに戻る
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <Icon name="alert-circle" size={20} style={{ color: 'var(--amber-400)' }} />
          <h1 style={{ font: 'var(--type-h2)', color: 'var(--fg)' }}>予定の変更を確認</h1>
        </div>
        <p style={{ font: 'var(--type-body)', color: 'var(--fg-secondary)', marginBottom: 16 }}>
          同じ時間帯に保存済みの予定があります。予定変更で上書きしてよろしいですか？上書きすると新しい方が優先されます。
        </p>

        {/* 一括操作 */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
          <Btn kind="accent" size="sm" icon="check" disabled={busy} onClick={overwriteAll}>すべて上書き</Btn>
          <Btn kind="ghost" size="sm" disabled={busy} onClick={keepAll}>すべて現状維持</Btn>
        </div>

        {/* 上書き候補一覧 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingBottom: 84 }}>
          {overwrites.map((o) => {
            const on = !!overwriteSel[o.existingId];
            return (
              <Card
                key={o.existingId}
                pad={0}
                style={{
                  overflow: 'hidden',
                  border: on ? '1px solid var(--accent)' : '1px solid var(--border)',
                  background: on ? 'var(--accent-fill-faint)' : undefined,
                  opacity: on ? 1 : 0.66,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px' }}>
                  <button
                    onClick={() => toggleOverwrite(o.existingId)}
                    aria-label={on ? 'この上書きをやめる' : 'この予定を上書きする'}
                    aria-pressed={on}
                    style={{
                      width: 20, height: 20, flexShrink: 0, borderRadius: 'var(--r-1)', cursor: 'pointer',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      background: on ? 'var(--accent)' : 'transparent',
                      border: '2px solid ' + (on ? 'var(--accent)' : 'var(--border-strong)'),
                      color: 'var(--on-cta)', padding: 0,
                    }}
                  >
                    {on && <Icon name="check" size={12} />}
                  </button>
                  <span style={{
                    font: 'var(--type-caption)', fontWeight: 600, padding: '4px 10px', borderRadius: 'var(--r-1)',
                    background: 'var(--accent-fill)', color: 'var(--teal-300)', whiteSpace: 'nowrap',
                  }}>{fmtSavedTime(o.content.startTime)}</span>
                </div>

                <div style={{ padding: '0 16px 12px 48px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {/* 現在（既存） */}
                  <div>
                    <div style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)' }}>現在</div>
                    <div style={{ textDecoration: 'line-through', opacity: 0.8 }}>
                      {partyLine(o.existing.companyName, o.existing.department, o.existing.contactName)}
                    </div>
                  </div>
                  {/* 新しい予定（こちらを優先） */}
                  <div>
                    <div style={{ font: 'var(--type-caption)', color: 'var(--accent)' }}>新しい予定</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Icon name="arrow-right" size={14} style={{ color: 'var(--accent)' }} />
                      {partyLine(o.content.companyName, o.content.department, o.content.contactName)}
                    </div>
                  </div>
                </div>

                {/* 個別 上書き / 現状維持 */}
                <div style={{ display: 'flex', gap: 8, padding: '0 16px 12px 48px' }}>
                  <Btn kind={on ? 'cta' : 'solid'} size="sm" disabled={busy}
                    onClick={() => setOverwriteSel((p) => ({ ...p, [o.existingId]: true }))}>
                    上書き
                  </Btn>
                  <Btn kind="ghost" size="sm" disabled={busy}
                    onClick={() => setOverwriteSel((p) => ({ ...p, [o.existingId]: false }))}>
                    現状維持
                  </Btn>
                </div>
              </Card>
            );
          })}
        </div>

        {/* フッタ: 確定 */}
        <div style={{
          position: 'sticky', bottom: 0, marginTop: 4, padding: '12px 0',
          background: 'linear-gradient(180deg, transparent, var(--bg) 30%)',
          display: 'flex', justifyContent: 'flex-end',
        }}>
          <Btn kind="cta" icon="check" disabled={busy} onClick={handleConfirmOverwrite}>
            {approvedCount > 0 ? `${approvedCount}件を上書きして進む` : 'そのままにして進む'}
          </Btn>
        </div>
      </>
    );
  };

  // S-063-15 生テキスト保管方式の取込画面（RC `calendar_rawtext_enabled` true 時）。
  //   貼付 → 日付分割プレビュー（編集可・PII マスク後）→ calendar_days 保存。日付別に閲覧・編集・削除。
  const renderRawImport = () => (
    <>
      <button
        onClick={onBack}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none',
          color: 'var(--fg-tertiary)', font: 'var(--type-ui)', cursor: 'pointer', marginBottom: 16, padding: 0,
        }}
      >
        <Icon name="arrow-left" size={16} />戻る
      </button>

      {/* C14 / S-063-18: 遡及反映後の元に戻す導線（生テキスト経路でも表示）。 */}
      {renderUndoBanner()}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <Icon name="calendar" size={20} style={{ color: 'var(--accent)' }} />
        <h1 style={{ font: 'var(--type-h2)', color: 'var(--fg)' }}>カレンダー取込</h1>
      </div>
      <p style={{ font: 'var(--type-body)', color: 'var(--fg-secondary)', marginBottom: 2 }}>
        予定表を貼ると、録音の相手先を補えます（任意）。
      </p>
      <p style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)', marginBottom: 20 }}>
        ※ 貼り付けたテキストは日付ごとに保存されます。メールアドレス・電話番号は自動で伏せられます。いつでも削除できます。
      </p>

      {planLoading ? (
        <div style={{ textAlign: 'center', color: 'var(--fg-tertiary)', padding: 48, font: 'var(--type-body)' }}>
          読み込み中...
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
          {!canUse && (
            <LockVeil
              message="カレンダー取込はスタンダードプランでご利用いただけます"
              cta="スタンダードにする"
              onUpsell={() => { window.location.href = '/pricing'; }}
            />
          )}

          {/* 貼り付け */}
          <textarea
            value={raw}
            disabled={!canUse}
            onChange={(e) => setRaw(e.target.value)}
            placeholder={'予定表をここに貼り付け\n例:\n6月19日（木）\n14:00-15:00 三菱電機 設備部\n16:00 日立 営業部'}
            aria-label="予定の貼り付け"
            rows={8}
            style={{
              width: '100%', boxSizing: 'border-box', padding: '12px 14px', borderRadius: 'var(--r-3)',
              background: 'var(--bg-elevated)', border: '1px solid var(--border-strong)',
              color: 'var(--fg)', font: 'var(--type-mono)', outline: 'none', resize: 'vertical',
            }}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
            <Btn kind="accent" icon="search" disabled={!canUse || !raw.trim()} onClick={handleSplit}>
              日付ごとに分ける
            </Btn>
          </div>

          {/* 日付分割プレビュー */}
          {dayDrafts !== null && (
            <div style={{ marginTop: 24 }}>
              <h3 style={{ font: 'var(--type-h3)', color: 'var(--fg)', fontWeight: 700, marginBottom: 12 }}>
                日付ごとのプレビュー（編集できます）
              </h3>

              {/* S-066-9 / DEC-066 真因(b): 曖昧/欠落見出しを検出したら、別の日に紛れ込まないよう
                  「日付なし」に隔離した旨を可視化し、ユーザーに日付確認を促す（fail-closed）。 */}
              {ambiguousHeaders.length > 0 && (
                <Card pad={14} style={{ marginBottom: 12, background: 'var(--cta-fill)', border: '1px solid var(--cta-line)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <Icon name="alert-circle" size={14} style={{ color: 'var(--amber-400)' }} />
                    <span style={{ font: 'var(--type-caption)', fontWeight: 700, color: 'var(--amber-400)' }}>
                      日付が読み取りにくい見出しが {ambiguousHeaders.length} 件あります
                    </span>
                  </div>
                  <p style={{ font: 'var(--type-caption)', color: 'var(--fg-secondary)', margin: 0 }}>
                    別の日に紛れ込まないよう、これらの行は下の「日付なし」にまとめています。日付を確認して割り当ててください。
                  </p>
                  <ul style={{ margin: '8px 0 0', paddingLeft: 18, color: 'var(--fg-tertiary)', font: 'var(--type-caption)' }}>
                    {ambiguousHeaders.slice(0, 5).map((a, i) => (
                      <li key={i} style={{ font: 'var(--type-mono)' }}>{a.line}</li>
                    ))}
                    {ambiguousHeaders.length > 5 && <li>ほか {ambiguousHeaders.length - 5} 件</li>}
                  </ul>
                </Card>
              )}

              {dayDrafts.length === 0 && !undatedDraft.trim() ? (
                <Card style={{ textAlign: 'center', padding: 32 }}>
                  <p style={{ font: 'var(--type-body)', color: 'var(--fg-secondary)' }}>
                    日付を読み取れませんでした
                  </p>
                  <p style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)', marginTop: 6 }}>
                    例: 「6月19日（木）」「6/19」「2026-06-19」のような日付の見出しを入れてください
                  </p>
                </Card>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {dayDrafts.map((d) => (
                    <Card key={d.dayId} pad={14}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <Icon name="calendar" size={14} style={{ color: 'var(--accent)' }} />
                        <span style={{ font: 'var(--type-caption)', fontWeight: 700, color: 'var(--fg)' }}>
                          {fmtDateLabel(d.dayId)}
                        </span>
                        <span style={{ flex: 1 }} />
                        <button onClick={() => removeDayDraft(d.dayId)} disabled={!canUse} title="この日を除外"
                          aria-label="この日を除外"
                          style={{ background: 'none', border: '1px solid var(--border-strong)', borderRadius: 'var(--r-2)', padding: '4px 8px', cursor: 'pointer', color: 'var(--fg-tertiary)', display: 'inline-flex' }}>
                          <Icon name="x" size={14} />
                        </button>
                      </div>
                      <textarea
                        value={d.text}
                        disabled={!canUse}
                        onChange={(e) => updateDayDraft(d.dayId, e.target.value)}
                        aria-label={`${d.dayId} の予定`}
                        rows={Math.min(10, Math.max(3, d.text.split('\n').length))}
                        style={{
                          width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 'var(--r-2)',
                          background: 'var(--bg-hover)', border: '1px solid var(--border-strong)',
                          color: 'var(--fg)', font: 'var(--type-mono)', outline: 'none', resize: 'vertical',
                        }}
                      />
                    </Card>
                  ))}

                  {/* 日付なし（日付を割り当てるか破棄） */}
                  {undatedDraft.trim() && (
                    <Card pad={14} style={{ background: 'var(--cta-fill)', border: '1px solid var(--cta-line)' }}>
                      <div style={{ font: 'var(--type-caption)', color: 'var(--amber-400)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Icon name="alert-circle" size={14} />
                        日付なし（保存するには日付を選んでください）
                      </div>
                      <textarea
                        value={undatedDraft}
                        disabled={!canUse}
                        onChange={(e) => setUndatedDraft(e.target.value)}
                        aria-label="日付なしの予定"
                        rows={Math.min(8, Math.max(2, undatedDraft.split('\n').length))}
                        style={{
                          width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 'var(--r-2)',
                          background: 'var(--bg-hover)', border: '1px solid var(--border-strong)',
                          color: 'var(--fg)', font: 'var(--type-mono)', outline: 'none', resize: 'vertical', marginBottom: 8,
                        }}
                      />
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ font: 'var(--type-caption)', color: 'var(--fg-secondary)' }}>日付を割り当て:</span>
                        <input
                          type="date"
                          value={undatedAssignDate}
                          disabled={!canUse}
                          onChange={(e) => setUndatedAssignDate(e.target.value)}
                          aria-label="日付なしの予定に割り当てる日付"
                          style={fieldStyle}
                        />
                      </div>
                    </Card>
                  )}
                </div>
              )}

              {/* 保存 */}
              {(dayDrafts.length > 0 || undatedDraft.trim()) && (
                <div style={{ marginTop: 18, display: 'flex', justifyContent: 'flex-end' }}>
                  <Btn kind="cta" icon="check" disabled={!canUse || busy} onClick={handleSaveDays}>
                    保存する
                  </Btn>
                </div>
              )}
            </div>
          )}

          {/* 保存済みの予定（日付別） */}
          {canUse && savedDays.length > 0 && (
            <div style={{ marginTop: 32, paddingTop: 20, borderTop: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                <h3 style={{ font: 'var(--type-h3)', color: 'var(--fg)', fontWeight: 700, flex: 1 }}>
                  保存済みの予定（日付別）
                </h3>
                <Btn kind="ghost" size="sm" icon="x" disabled={busy} onClick={handleDeleteAllDays}>
                  すべて削除
                </Btn>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {savedDays.map((d) => {
                  // S-066-8: 保存時刻から鮮度を導出（未解決なら null＝非表示）。表示のみ・自動操作はしない。
                  const fresh = pasteFreshness(d.updatedAt);
                  return (
                  <Card key={d.dayId} pad={12}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Icon name="calendar" size={14} style={{ color: 'var(--accent)' }} />
                      <div style={{ flex: 1, display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ font: 'var(--type-ui)', fontWeight: 600, color: 'var(--fg)' }}>
                          {fmtDateLabel(d.dayId)}
                        </span>
                        {/* S-066-8: 最終更新日（相対）。古ければ amber で注意喚起（DEC-009 精度/%なし）。 */}
                        {fresh && (
                          <span style={{ font: 'var(--type-caption)', color: fresh.stale ? 'var(--amber-400)' : 'var(--fg-tertiary)' }}>
                            {fresh.label}
                          </span>
                        )}
                      </div>
                      {editDayId === d.dayId ? (
                        <>
                          <Btn kind="cta" size="sm" icon="check" disabled={busy} onClick={handleSaveEditDay}>保存</Btn>
                          <Btn kind="ghost" size="sm" disabled={busy} onClick={cancelEditDay}>取消</Btn>
                        </>
                      ) : (
                        <>
                          <Btn kind="ghost" size="sm" icon="align-left" disabled={busy} onClick={() => startEditDay(d)}>編集</Btn>
                          <Btn kind="ghost" size="sm" icon="x" disabled={busy} onClick={() => handleDeleteDay(d.dayId)}>削除</Btn>
                        </>
                      )}
                    </div>
                    {/* S-066-8: 古いペースト（腐ったスナップショット）への注意。表示のみ＝自動削除/再取込はしない。 */}
                    {fresh?.stale && editDayId !== d.dayId && (
                      <div role="note" style={{
                        marginTop: 8, padding: '8px 10px', borderRadius: 'var(--r-2)',
                        background: 'var(--cta-fill)', border: '1px solid var(--cta-line)',
                        font: 'var(--type-caption)', color: 'var(--fg-secondary)', lineHeight: 1.6,
                        display: 'flex', alignItems: 'flex-start', gap: 6,
                      }}>
                        <Icon name="alert-circle" size={14} style={{ color: 'var(--amber-400)', flexShrink: 0, marginTop: 1 }} />
                        <span>{STALE_PASTE_NOTICE}</span>
                      </div>
                    )}
                    {editDayId === d.dayId ? (
                      <textarea
                        value={editDayText}
                        onChange={(e) => setEditDayText(e.target.value)}
                        aria-label={`${d.dayId} の予定を編集`}
                        rows={Math.min(12, Math.max(3, editDayText.split('\n').length))}
                        style={{
                          width: '100%', boxSizing: 'border-box', marginTop: 8, padding: '10px 12px', borderRadius: 'var(--r-2)',
                          background: 'var(--bg-hover)', border: '1px solid var(--border-strong)',
                          color: 'var(--fg)', font: 'var(--type-mono)', outline: 'none', resize: 'vertical',
                        }}
                      />
                    ) : (
                      <div style={{
                        font: 'var(--type-caption)', color: 'var(--fg-secondary)', marginTop: 6,
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        maxHeight: 120, overflow: 'hidden',
                      }}>
                        {d.rawText}
                      </div>
                    )}
                  </Card>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );

  return (
    <div style={{ maxWidth: 880, margin: '0 auto', padding: '20px 24px 96px' }}>
      {rawMode
        ? (view === 'confirm' ? renderConfirm() : renderRawImport())
        : view === 'overwrite' ? renderOverwrite() : view === 'confirm' ? renderConfirm() : (
      <>
      <button
        onClick={onBack}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none',
          color: 'var(--fg-tertiary)', font: 'var(--type-ui)', cursor: 'pointer', marginBottom: 16, padding: 0,
        }}
      >
        <Icon name="arrow-left" size={16} />戻る
      </button>

      {/* C14: 直近の反映を1タップで元に戻す導線（反映後に表示）。 */}
      {renderUndoBanner()}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <Icon name="calendar" size={20} style={{ color: 'var(--accent)' }} />
        <h1 style={{ font: 'var(--type-h2)', color: 'var(--fg)' }}>カレンダー取込</h1>
      </div>
      <p style={{ font: 'var(--type-body)', color: 'var(--fg-secondary)', marginBottom: 2 }}>
        予定表をコピーして貼り付けると、録音の相手先を補えます（任意）。
      </p>
      <p style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)', marginBottom: 20 }}>
        ※ 取り込んだ予定は相手先の参考にのみ使い、一定期間後に消えます。
      </p>

      {/* plan ローディング中はスケルトン（ルート画面マウント遅延の既存方針と整合）。 */}
      {planLoading ? (
        <div style={{ textAlign: 'center', color: 'var(--fg-tertiary)', padding: 48, font: 'var(--type-body)' }}>
          読み込み中...
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
          {/* free / light は LockVeil で覆い、アップセル誘導（C12）。 */}
          {!canUse && (
            <LockVeil
              message="カレンダー取込はスタンダードプランでご利用いただけます"
              cta="スタンダードにする"
              onUpsell={() => { window.location.href = '/pricing'; }}
            />
          )}

          {/* 貼り付け */}
          <textarea
            value={raw}
            disabled={!canUse}
            onChange={(e) => setRaw(e.target.value)}
            placeholder={'Copilotの予定をここに貼り付け\n例: 【WEB】三菱電機 設備部 田中太郎（14:00-15:00）'}
            aria-label="予定の貼り付け"
            rows={7}
            style={{
              width: '100%', boxSizing: 'border-box', padding: '12px 14px', borderRadius: 'var(--r-3)',
              background: 'var(--bg-elevated)', border: '1px solid var(--border-strong)',
              color: 'var(--fg)', font: 'var(--type-mono)', outline: 'none', resize: 'vertical',
            }}
          />
          {/* 入力上限超過の事前警告（サイレント切り捨て防止）。 */}
          {canUse && overLimit && (
            <div
              role="alert"
              style={{
                marginTop: 10, padding: '10px 12px', borderRadius: 'var(--r-3)',
                background: 'var(--cta-fill)', border: '1px solid var(--cta-line)',
                font: 'var(--type-caption)', color: 'var(--amber-400)',
                display: 'flex', alignItems: 'center', gap: 8,
              }}
            >
              <Icon name="alert-circle" size={14} />
              {`文字数が上限（${MAX_COPILOT_RAW_CHARS.toLocaleString()}字）を超えています（現在 ${raw.length.toLocaleString()}字）。このままだと末尾が読み取られません。数回に分けて取り込んでください。`}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
            <Btn kind="accent" icon="search" disabled={!canParse || reading} onClick={handleParse}>
              {reading ? '読み取り中…' : '読み取る'}
            </Btn>
          </div>

          {/* 読み取り結果 */}
          {rows !== null && (
            <div style={{ marginTop: 24 }}>
              <h3 style={{ font: 'var(--type-h3)', color: 'var(--fg)', fontWeight: 700, marginBottom: 12 }}>
                読み取り結果（編集できます）
              </h3>

              {rows.length === 0 ? (
                <Card style={{ textAlign: 'center', padding: 32 }}>
                  <p style={{ font: 'var(--type-body)', color: 'var(--fg-secondary)' }}>
                    予定を読み取れませんでした。形式をご確認ください
                  </p>
                  <p style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)', marginTop: 6 }}>
                    例: 会社名 部署 担当者（14:00-15:00）
                  </p>
                </Card>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {rows.map((r, i) => {
                    const review = rowNeedsReview(r);
                    // 日付が変わるところに日付ラベルを挟む（複数日一括取込の見分け）。先頭は常に表示。
                    const showDate = !!r.dateIso && (i === 0 || rows[i - 1].dateIso !== r.dateIso);
                    return (
                      <React.Fragment key={r.id}>
                      {showDate && (
                        <div style={{ font: 'var(--type-caption)', color: 'var(--fg-secondary)', fontWeight: 700, marginTop: i === 0 ? 0 : 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Icon name="calendar" size={13} style={{ color: 'var(--accent)' }} />
                          {fmtDateLabel(r.dateIso)}
                        </div>
                      )}
                      <Card
                        pad={14}
                        style={{
                          background: review ? 'var(--cta-fill)' : undefined,
                          border: review ? '1px solid var(--cta-line)' : undefined,
                        }}
                      >
                        {review && (
                          <div style={{ font: 'var(--type-caption)', color: 'var(--amber-400)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Icon name="alert-circle" size={14} />
                            {!r.companyName.trim() ? '会社名を確認してください' : '時刻を確認してください'}
                          </div>
                        )}
                        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr auto', gap: 8, alignItems: 'center' }}>
                          <input value={r.companyName} disabled={!canUse} placeholder="会社名" aria-label="会社名"
                            onChange={(e) => updateRow(r.id, { companyName: e.target.value })} style={fieldStyle} />
                          <input value={r.department} disabled={!canUse} placeholder="部署" aria-label="部署"
                            onChange={(e) => updateRow(r.id, { department: e.target.value })} style={fieldStyle} />
                          <input value={r.contactName} disabled={!canUse} placeholder="担当者" aria-label="担当者"
                            onChange={(e) => updateRow(r.id, { contactName: e.target.value })} style={fieldStyle} />
                          <button onClick={() => removeRow(r.id)} disabled={!canUse} title="この行を除外"
                            aria-label="この行を除外"
                            style={{ background: 'none', border: '1px solid var(--border-strong)', borderRadius: 'var(--r-2)', padding: '6px 8px', cursor: 'pointer', color: 'var(--fg-tertiary)', display: 'inline-flex' }}>
                            <Icon name="x" size={14} />
                          </button>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                          <input type="time" value={r.startHHMM} disabled={!canUse} aria-label="開始時刻"
                            onChange={(e) => updateRow(r.id, { startHHMM: e.target.value })} style={fieldStyle} />
                          <span style={{ color: 'var(--fg-tertiary)' }}>−</span>
                          <input type="time" value={r.endHHMM} disabled={!canUse} aria-label="終了時刻"
                            onChange={(e) => updateRow(r.id, { endHHMM: e.target.value })} style={fieldStyle} />
                          {r.recurrence && (
                            <span style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)' }}>{r.recurrence}</span>
                          )}
                        </div>
                      </Card>
                      </React.Fragment>
                    );
                  })}
                </div>
              )}

              {/* 取り込めなかった行 */}
              {parseErrors.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)', marginBottom: 6 }}>
                    取り込めなかった行
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {parseErrors.map((pe, i) => (
                      <div key={i} style={{ font: 'var(--type-caption)', color: 'var(--fg-secondary)', padding: '6px 10px', borderRadius: 'var(--r-2)', background: 'var(--overlay-soft)' }}>
                        {pe.line || pe.reason}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 保存 */}
              {rows.length > 0 && (
                <div style={{ marginTop: 18 }}>
                  {writableCount === 0 && (
                    <p style={{ font: 'var(--type-caption)', color: 'var(--amber-400)', marginBottom: 8 }}>
                      保存できる予定がありませんでした（会社名・時刻をご確認ください）
                    </p>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Btn kind="cta" icon="check" disabled={!canUse || busy || writableCount === 0} onClick={handleSave}>
                      保存する
                    </Btn>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 保存済みの予定 */}
          {canUse && saved.length > 0 && (
            <div style={{ marginTop: 32, paddingTop: 20, borderTop: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                <h3 style={{ font: 'var(--type-h3)', color: 'var(--fg)', fontWeight: 700, flex: 1 }}>
                  保存済みの予定
                </h3>
                <Btn kind="ghost" size="sm" icon="x" disabled={busy} onClick={handleDeleteAll}>
                  すべて削除
                </Btn>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {saved.map((ev) => (
                  <Card key={ev.id} pad={12} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ font: 'var(--type-ui)', color: 'var(--fg)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {ev.companyName}
                        {ev.department && <span style={{ color: 'var(--fg-tertiary)' }}> ・ {ev.department}</span>}
                        {ev.contactName && <span style={{ color: 'var(--fg-tertiary)' }}> ・ {ev.contactName}</span>}
                      </div>
                      <div style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)', marginTop: 2 }}>
                        {fmtSavedTime(ev.startTime)}
                      </div>
                    </div>
                    <Btn kind="ghost" size="sm" disabled={busy} onClick={() => handleDeleteBatch(ev.batchId)}>
                      取消
                    </Btn>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      </>
      )}

      {toast && (
        <div style={{
          position: 'fixed', left: '50%', bottom: 28, transform: 'translateX(-50%)', zIndex: 80,
          background: 'var(--bg-elevated)', border: '1px solid var(--border-strong)', borderRadius: 'var(--r-pill)',
          padding: '10px 20px', boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
          font: 'var(--type-ui)', color: 'var(--fg)',
        }}>
          {toast}
        </div>
      )}
    </div>
  );
};

export default CalendarImportPage;
